import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useRideDialog } from './RideDialogProvider';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Checkbox,
  Toolbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Alert,
  Grid
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Repeat as RecurringIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  List as ListIcon,
  CalendarMonth as CalendarIcon,
  LocalHospital as MedicalIcon,
  WarningAmber as WarningIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import {
  fetchRecurringRides,
  pauseRecurringOrder,
  resumeRecurringOrder,
  cancelRecurringOrder,
  deleteRecurringOrder,
  deleteRide
} from '../../redux/slices/ridesSlice';
import { formatVehicleWithCapabilities } from '../../config/vehicleTypes';
import {
  APPROVAL_STATUS_OPTIONS,
  getOption
} from '../medicalTransportOrders/medicalTransportOrderOptions';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 'bold',
  ...(status === 'active' && {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  }),
  ...(status === 'paused' && {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  }),
  ...(status === 'cancelled' && {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  }),
}));

const MEDICAL_PLANNED_STATUSES = new Set([
  'scheduled',
  'pre_accepted',
  'accepted',
  'arrived',
  'in_progress',
  'open',
  'dispatcher_pending'
]);

const MEDICAL_CANCELLED_STATUSES = new Set(['cancelled', 'cancelled_by_driver']);

const LEGACY_STRUCTURE_FIELDS = [
  { key: 'organizationId', label: 'Organisation' },
  { key: 'is_active', label: 'Aktiv-Flag' },
  { key: 'driverVehicleId', label: 'Fahrzeugreferenz' },
  { key: 'fare', label: 'Fahrpreis' },
  { key: 'estimatedTimeMinutes', label: 'Fahrtdauer' },
  { key: 'distanceKm', label: 'Strecke' },
  { key: 'notes', label: 'Notizen' },
  { key: 'specialCapabilities', label: 'Spezialfähigkeiten' },
  { key: 'isKrankenfahrt', label: 'Krankenfahrt-Flag' },
  { key: 'medicalTransportOrderId', label: 'Verordnungsreferenz' },
  { key: 'medicalTransportSnapshot', label: 'Verordnungsdaten' },
  { key: 'medicalTransportBillingStatus', label: 'Abrechnungsstatus' },
  { key: 'medicalTransportProofStatus', label: 'Nachweisstatus' },
  { key: 'isRechnungsfahrt', label: 'Rechnungsfahrt-Flag' },
  { key: 'isPhoneCustomer', label: 'Kundentyp' }
];

const RecurringOrdersList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openRideDialog } = useRideDialog() || {};
  const { recurringOrders, isLoading, error } = useSelector(state => state.rides);
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [ridesDialog, setRidesDialog] = useState(false);
  const [selectedRideIds, setSelectedRideIds] = useState([]);
  const [ridesConfirmDialog, setRidesConfirmDialog] = useState(false);
  const [ridesActionType, setRidesActionType] = useState(null);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterDriver, setFilterDriver] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    dispatch(fetchRecurringRides());
  }, [dispatch]);

  const handleActionClick = (event, order) => {
    setSelectedOrder(order);
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionClose = () => {
    setActionMenuAnchor(null);
    setSelectedOrder(null);
  };

  const handleActionSelect = (type) => {
    setActionType(type);
    setActionMenuAnchor(null);
    
    if (type === 'view') {
      setDetailsDialog(true);
    } else if (type === 'rides') {
      setRidesDialog(true);
    } else if (type === 'edit') {
      // Navigiere zur Bearbeitungsseite
      navigate(`/fahrten/dauerauftraege/${selectedOrder.id}/bearbeiten`);
    } else {
      setConfirmDialog(true);
    }
  };

  const handleConfirmAction = async () => {
    if (!selectedOrder || !actionType) return;

    try {
      let result = null;

      switch (actionType) {
        case 'pause':
          result = await dispatch(pauseRecurringOrder(selectedOrder.id));
          break;
        case 'resume':
          result = await dispatch(resumeRecurringOrder(selectedOrder.id));
          break;
        case 'cancel':
          result = await dispatch(cancelRecurringOrder(selectedOrder.id));
          break;
        case 'delete':
          result = await dispatch(deleteRecurringOrder(selectedOrder.id));
          break;
        default:
          break;
      }

      if (result?.error) {
        throw new Error(result.error.message || 'Aktion konnte nicht ausgeführt werden');
      }
      
      // Aktualisiere die Liste
      await dispatch(fetchRecurringRides());
    } catch (error) {
      console.error('Fehler bei Aktion:', error);
    }

    setConfirmDialog(false);
    setSelectedOrder(null);
    setActionType(null);
  };

  const handleCloseDialogs = () => {
    setConfirmDialog(false);
    setDetailsDialog(false);
    setRidesDialog(false);
    setSelectedOrder(null);
    setActionType(null);
    setSelectedRideIds([]);
    setRidesConfirmDialog(false);
    setRidesActionType(null);
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterDriver('');
    setFilterStatus('');
  };

  const handleToggleRideSelect = (rideId) => {
    setSelectedRideIds(prev =>
      prev.includes(rideId)
        ? prev.filter(id => id !== rideId)
        : [...prev, rideId]
    );
  };

  const handleToggleSelectAll = () => {
    if (!selectedOrder?.rides) return;
    if (selectedRideIds.length === selectedOrder.rides.length) {
      setSelectedRideIds([]);
    } else {
      setSelectedRideIds(selectedOrder.rides.map(r => r.id));
    }
  };

  const handleBulkAction = (type) => {
    setRidesActionType(type);
    setRidesConfirmDialog(true);
  };

  const handleConfirmBulkAction = async () => {
    if (!selectedRideIds.length || !ridesActionType) return;

    try {
      if (ridesActionType === 'delete') {
        for (const rideId of selectedRideIds) {
          await dispatch(deleteRide(rideId));
        }
      } else if (ridesActionType === 'cancel') {
        // Storniere über updateDoc direkt in Firestore
        const { updateDoc, doc, Timestamp } = await import('firebase/firestore');
        const { db } = await import('../../firebase/config');
        for (const rideId of selectedRideIds) {
          const rideRef = doc(db, 'rides', rideId);
          await updateDoc(rideRef, {
            status: 'cancelled',
            cancelledAt: Timestamp.now(),
            cancelReason: 'Einzelfahrt aus Dauerauftrag storniert'
          });
        }
      }

      // Daueraufträge neu laden und Auswahl zurücksetzen
      await dispatch(fetchRecurringRides());
      setSelectedRideIds([]);
      setRidesConfirmDialog(false);
      setRidesActionType(null);

      // Aktualisiere selectedOrder mit neuen Daten
      // Dialog schließen da die Daten sich geändert haben
      setRidesDialog(false);
      setSelectedOrder(null);
    } catch (error) {
      console.error('Fehler bei Bulk-Aktion:', error);
    }
  };

  // Gefilterte Fahrten berechnen
  const getFilteredRides = () => {
    if (!selectedOrder?.rides) return [];
    return selectedOrder.rides.filter(ride => {
      const rideDate = formatRideDateTime(ride.scheduledTime);

      if (filterDateFrom) {
        const from = new Date(filterDateFrom);
        from.setHours(0, 0, 0, 0);
        if (rideDate < from) return false;
      }
      if (filterDateTo) {
        const to = new Date(filterDateTo);
        to.setHours(23, 59, 59, 999);
        if (rideDate > to) return false;
      }
      if (filterDriver) {
        const driverName = ride.driverName || 'Nicht zugewiesen';
        if (driverName !== filterDriver) return false;
      }
      if (filterStatus) {
        if (ride.status !== filterStatus) return false;
      }
      return true;
    });
  };

  // Alle eindeutigen Fahrer aus den Rides des ausgewählten Dauerauftrags
  const getUniqueDrivers = () => {
    if (!selectedOrder?.rides) return [];
    const drivers = new Set();
    selectedOrder.rides.forEach(ride => {
      drivers.add(ride.driverName || 'Nicht zugewiesen');
    });
    return [...drivers].sort();
  };

  // Alle eindeutigen Status aus den Rides
  const getUniqueStatuses = () => {
    if (!selectedOrder?.rides) return [];
    const statuses = new Set();
    selectedOrder.rides.forEach(ride => {
      statuses.add(ride.status);
    });
    return [...statuses];
  };

  const getOrderStatus = (order) => {
    if (!order) {
      return 'unknown';
    }
    
    // Prüfe zuerst pausierte/aktive Fahrten BEVOR completed Check
    if (order.rides && order.rides.length > 0) {
      const futureRides = order.rides.filter(ride => {
        // Konvertiere Firestore Timestamp zu JavaScript Date für Vergleich
        const rideDate = ride.scheduledTime?.toDate ? ride.scheduledTime.toDate() : new Date(ride.scheduledTime);
        const isFuture = rideDate > new Date();
        const hasValidStatus = ['scheduled', 'paused', 'pre_accepted'].includes(ride.status);
        return isFuture && hasValidStatus;
      });
      
      // Wenn es zukünftige Fahrten gibt
      if (futureRides.length > 0) {
        const allPaused = futureRides.every(ride => ride.status === 'paused');
        const allScheduled = futureRides.every(ride => ['scheduled', 'pre_accepted'].includes(ride.status));
        
        if (allPaused) {
          return 'paused';
        } else if (allScheduled) {
          return 'active';
        } else {
          // Gemischt - einige pausiert, einige geplant
          return 'active';
        }
      }
    }
    
    // Nur wenn KEINE zukünftigen Fahrten vorhanden sind
    if (order.active_rides === 0) {
      return 'completed';
    }

    return 'active';
  };

  const hasFutureRideWithStatus = (order, statuses) => {
    if (!order?.rides?.length) return false;
    const now = new Date();
    return order.rides.some(ride => {
      const rideDate = ride.scheduledTime?.toDate ? ride.scheduledTime.toDate() : new Date(ride.scheduledTime);
      return rideDate > now && statuses.includes(ride.status);
    });
  };

  const getLegacyStructureInfo = (order) => {
    const now = new Date();
    const relevantRides = (order?.rides || []).filter(ride => {
      const rideDate = ride.scheduledTime?.toDate ? ride.scheduledTime.toDate() : new Date(ride.scheduledTime);
      return rideDate > now && ['scheduled', 'paused', 'pre_accepted'].includes(ride.status);
    });

    const missingLabels = new Set();
    let affectedRides = 0;

    relevantRides.forEach(ride => {
      const missingForRide = LEGACY_STRUCTURE_FIELDS.filter(({ key }) => (
        !Object.prototype.hasOwnProperty.call(ride, key)
      ));

      if (missingForRide.length > 0) {
        affectedRides += 1;
        missingForRide.forEach(({ label }) => missingLabels.add(label));
      }
    });

    return {
      hasLegacyStructure: affectedRides > 0,
      affectedRides,
      missingLabels: [...missingLabels]
    };
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Aktiv';
      case 'paused':
        return 'Pausiert';
      case 'cancelled':
        return 'Storniert';
      case 'completed':
        return 'Abgeschlossen';
      default:
        return 'Unbekannt';
    }
  };

  const getRideStatusLabel = (status) => {
    switch (status) {
      case 'scheduled':
        return 'Geplant';
      case 'pre_accepted':
        return 'Vom Fahrer angenommen';
      case 'accepted':
        return 'Akzeptiert';
      case 'arrived':
        return 'Angekommen';
      case 'in_progress':
        return 'Zielfahrt';
      case 'completed':
        return 'Abgeschlossen';
      case 'cancelled':
        return 'Storniert';
      case 'cancelled_by_driver':
        return 'Vom Fahrer storniert';
      case 'open':
        return 'Offen';
      default:
        return 'Unbekannt';
    }
  };

  const getActionTypeLabel = (type) => {
    switch (type) {
      case 'pause':
        return 'pausieren';
      case 'resume':
        return 'reaktivieren';
      case 'cancel':
        return 'stornieren';
      case 'delete':
        return 'löschen';
      default:
        return type;
    }
  };

  const formatVehicleType = (vehicleType, specialCapabilities) => {
    return formatVehicleWithCapabilities(vehicleType, specialCapabilities || []);
  };

  const getMedicalTransportStats = (order) => {
    const rides = order?.rides || [];
    const medicalRides = rides.filter(ride => ride.medicalTransportOrderId);
    const snapshot = order?.medicalTransportSnapshot || medicalRides[0]?.medicalTransportSnapshot || null;
    const medicalTransportOrderId = order?.medicalTransportOrderId || medicalRides[0]?.medicalTransportOrderId || null;

    if (!medicalTransportOrderId) {
      return {
        linked: false,
        snapshot: null,
        medicalTransportOrderId: null,
        completed: 0,
        planned: 0,
        cancelled: 0,
        counted: 0,
        maxRides: 0,
        remaining: null
      };
    }

    const completed = medicalRides.filter(ride => ride.status === 'completed').length;
    const cancelled = medicalRides.filter(ride => MEDICAL_CANCELLED_STATUSES.has(ride.status)).length;
    const planned = medicalRides.filter(ride => MEDICAL_PLANNED_STATUSES.has(ride.status)).length;
    const counted = completed + planned;
    const maxRides = Number(snapshot?.maxRides) || 0;

    return {
      linked: true,
      snapshot,
      medicalTransportOrderId,
      completed,
      planned,
      cancelled,
      counted,
      maxRides,
      remaining: maxRides > 0 ? Math.max(maxRides - counted, 0) : null,
      exceeded: maxRides > 0 && counted > maxRides
    };
  };

  const renderMedicalTransportCell = (order) => {
    const stats = getMedicalTransportStats(order);
    if (!stats.linked) {
      return (
        <Chip
          size="small"
          label="Keine Verordnung"
          variant="outlined"
        />
      );
    }

    const approval = getOption(APPROVAL_STATUS_OPTIONS, stats.snapshot?.approvalStatus);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip
            size="small"
            icon={<MedicalIcon />}
            label={approval.label}
            color={approval.color}
            variant={approval.color === 'default' ? 'outlined' : 'filled'}
          />
          <Chip
            size="small"
            label={stats.remaining === null ? `${stats.counted} belegt` : `${stats.counted}/${stats.maxRides}`}
            color={stats.exceeded ? 'error' : 'default'}
            variant="outlined"
          />
        </Box>
        <Typography variant="caption" color={stats.exceeded ? 'error' : 'text.secondary'}>
          {stats.remaining === null
            ? 'Kein Kontingent hinterlegt'
            : `${stats.remaining} Restfahrten`}
        </Typography>
      </Box>
    );
  };

  const formatRideDateTime = (scheduledTime) => {
    const rideDate = scheduledTime?.toDate ? scheduledTime.toDate() : new Date(scheduledTime);
    return rideDate;
  };

  if (isLoading) {
    return <Loading message="Daueraufträge werden geladen..." />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Daueraufträge"
        message={error}
        onRetry={() => dispatch(fetchRecurringRides())}
      />
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Daueraufträge"
        subtitle="Verwaltung wiederkehrender Fahrten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' },
          { label: 'Daueraufträge', path: '/fahrten/dauerauftraege' }
        ]}
        primaryButton={{
          label: 'Neuen Dauerauftrag erstellen',
          icon: <RecurringIcon />,
          onClick: () => openRideDialog?.({ mode: 'create' })
        }}
      />

      {recurringOrders.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <RecurringIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Keine Daueraufträge vorhanden
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Erstellen Sie Ihren ersten Dauerauftrag für wiederkehrende Fahrten wie Kranken- oder Dialysefahrten.
          </Typography>
          <Button
            variant="contained"
            startIcon={<RecurringIcon />}
            sx={{ mt: 2 }}
            onClick={() => openRideDialog?.({ mode: 'create' })}
          >
            Dauerauftrag erstellen
          </Button>
        </Paper>
      ) : (
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Kunde</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Fahrzeugtyp</TableCell>
                <TableCell>Verordnung</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Aktive Fahrten</TableCell>
                <TableCell>Gesamt Fahrten</TableCell>
                <TableCell>Erstellt am</TableCell>
                <TableCell align="center">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recurringOrders.map((order) => {
                const status = getOrderStatus(order);
                const legacyInfo = getLegacyStructureInfo(order);
                return (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {order.customer_name || 'Unbekannt'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          <LocationIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          {order.pickup_address}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          → {order.destination_address}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatVehicleType(order.vehicle_type, order.special_capabilities)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {renderMedicalTransportCell(order)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                        <StatusChip
                          label={getStatusLabel(status)}
                          size="small"
                          status={status}
                        />
                        {legacyInfo.hasLegacyStructure && (
                          <Chip
                            icon={<WarningIcon />}
                            label="Alte Datenstruktur"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {order.active_rides}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.total_rides}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.created_at ? 
                          new Date(order.created_at.toDate?.() || order.created_at).toLocaleDateString('de-DE') :
                          'Unbekannt'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={(e) => handleActionClick(e, order)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </StyledTableContainer>
      )}

      {/* Aktions-Menü */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => handleActionSelect('view')}>
          <ViewIcon sx={{ mr: 1 }} />
          Details anzeigen
        </MenuItem>
        <MenuItem onClick={() => handleActionSelect('rides')}>
          <ListIcon sx={{ mr: 1 }} />
          Alle Fahrten anzeigen
        </MenuItem>
        <MenuItem onClick={() => handleActionSelect('edit')}>
          <EditIcon sx={{ mr: 1 }} />
          Bearbeiten
        </MenuItem>
        {selectedOrder && hasFutureRideWithStatus(selectedOrder, ['scheduled']) && (
          <MenuItem onClick={() => handleActionSelect('pause')}>
            <PauseIcon sx={{ mr: 1 }} />
            Pausieren
          </MenuItem>
        )}
        {selectedOrder && hasFutureRideWithStatus(selectedOrder, ['paused']) && (
          <MenuItem onClick={() => handleActionSelect('resume')}>
            <PlayIcon sx={{ mr: 1 }} />
            Reaktivieren
          </MenuItem>
        )}
        <MenuItem
          onClick={() => handleActionSelect('cancel')}
          sx={{ color: 'error.main' }}
        >
          <CancelIcon sx={{ mr: 1 }} />
          Stornieren
        </MenuItem>
        <MenuItem
          onClick={() => handleActionSelect('delete')}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>

      {/* Bestätigungs-Dialog */}
      <Dialog open={confirmDialog} onClose={handleCloseDialogs}>
        <DialogTitle>
          Dauerauftrag {getActionTypeLabel(actionType)}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie den Dauerauftrag für{' '}
            <strong>{selectedOrder?.customer_name}</strong>{' '}
            {getActionTypeLabel(actionType)} möchten?
          </Typography>
          {actionType === 'cancel' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Alle zukünftigen Fahrten werden storniert. Diese Aktion kann nicht rückgängig gemacht werden.
            </Alert>
          )}
          {actionType === 'delete' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Alle {selectedOrder?.total_rides || 0} Fahrten dieses Dauerauftrags werden unwiderruflich aus der Datenbank gelöscht. Diese Aktion kann nicht rückgängig gemacht werden!
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleConfirmAction} 
            color={['cancel', 'delete'].includes(actionType) ? 'error' : 'primary'}
            variant="contained"
          >
            {getActionTypeLabel(actionType)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details-Dialog */}
      <Dialog open={detailsDialog} onClose={handleCloseDialogs} maxWidth="md" fullWidth>
        <DialogTitle>
          Dauerauftrag Details
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Grid container spacing={3}>
              {(() => {
                const medicalStats = getMedicalTransportStats(selectedOrder);
                const approval = getOption(APPROVAL_STATUS_OPTIONS, medicalStats.snapshot?.approvalStatus);
                const legacyInfo = getLegacyStructureInfo(selectedOrder);
	
                return (
                  <>
              {legacyInfo.hasLegacyStructure && (
                <Grid size={12}>
                  <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => navigate(`/fahrten/dauerauftraege/${selectedOrder.id}/bearbeiten`)}
                      >
                        Bearbeiten
                      </Button>
                    }
                  >
                    Dieser Dauerauftrag enthält {legacyInfo.affectedRides} zukünftige Fahrt(en) mit alter Datenstruktur.
                    Bitte einmal prüfen und speichern, damit fehlende Felder ergänzt werden.
                    {legacyInfo.missingLabels.length > 0 && (
                      <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                        Fehlende Feldgruppen: {legacyInfo.missingLabels.slice(0, 6).join(', ')}
                        {legacyInfo.missingLabels.length > 6 ? ' ...' : ''}
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" gutterBottom>
                  Kundeninformation
                </Typography>
                <Typography><strong>Name:</strong> {selectedOrder.customer_name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" gutterBottom>
                  Fahrtinformation
                </Typography>
                <Typography><strong>Von:</strong> {selectedOrder.pickup_address}</Typography>
                <Typography><strong>Nach:</strong> {selectedOrder.destination_address}</Typography>
                <Typography><strong>Fahrzeugtyp:</strong> {formatVehicleType(selectedOrder.vehicle_type, selectedOrder.special_capabilities)}</Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>
                  Verordnung
                </Typography>
                {medicalStats.linked ? (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Chip size="small" label={approval.label} color={approval.color} />
                      <Chip
                        size="small"
                        label={medicalStats.remaining === null ? `${medicalStats.counted} Fahrten belegt` : `${medicalStats.counted}/${medicalStats.maxRides} belegt`}
                        color={medicalStats.exceeded ? 'error' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                    <Typography>
                      <strong>Patient:</strong> {medicalStats.snapshot?.patientName || selectedOrder.customer_name}
                    </Typography>
                    <Typography>
                      <strong>Krankenkasse:</strong> {medicalStats.snapshot?.healthInsuranceName || 'Nicht gesetzt'}
                    </Typography>
                    <Typography>
                      <strong>Kontingent:</strong>{' '}
                      {medicalStats.remaining === null
                        ? 'Kein Kontingent hinterlegt'
                        : `${medicalStats.remaining} Restfahrten`}
                    </Typography>
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => navigate(`/krankenfahrten/${medicalStats.medicalTransportOrderId}`)}
                    >
                      Verordnung öffnen
                    </Button>
                  </Box>
                ) : (
                  <Alert severity="info">Dieser Dauerauftrag ist keiner Verordnung zugeordnet.</Alert>
                )}
              </Grid>
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>
                  Statistiken
                </Typography>
                <Typography><strong>Aktive Fahrten:</strong> {selectedOrder?.active_rides || 0}</Typography>
                <Typography><strong>Gesamt Fahrten:</strong> {selectedOrder?.total_rides || 0}</Typography>
                <Typography><strong>Status:</strong> {getStatusLabel(getOrderStatus(selectedOrder))}</Typography>
              </Grid>
                  </>
                );
              })()}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fahrten-Dialog */}
      <Dialog open={ridesDialog} onClose={handleCloseDialogs} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarIcon sx={{ mr: 1 }} />
            Alle Fahrten - {selectedOrder?.customer_name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (() => {
            const filteredRides = getFilteredRides();
            return (
            <>
              {/* Filter-Leiste */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  label="Datum von"
                  type="date"
                  size="small"
                  value={filterDateFrom}
                  onChange={(e) => { setFilterDateFrom(e.target.value); setSelectedRideIds([]); }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  label="Datum bis"
                  type="date"
                  size="small"
                  value={filterDateTo}
                  onChange={(e) => { setFilterDateTo(e.target.value); setSelectedRideIds([]); }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ minWidth: 160 }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Fahrer</InputLabel>
                  <Select
                    value={filterDriver}
                    label="Fahrer"
                    onChange={(e) => { setFilterDriver(e.target.value); setSelectedRideIds([]); }}
                  >
                    <MenuItem value="">Alle</MenuItem>
                    {getUniqueDrivers().map(driver => (
                      <MenuItem key={driver} value={driver}>{driver}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => { setFilterStatus(e.target.value); setSelectedRideIds([]); }}
                  >
                    <MenuItem value="">Alle</MenuItem>
                    {getUniqueStatuses().map(status => (
                      <MenuItem key={status} value={status}>{getRideStatusLabel(status)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(filterDateFrom || filterDateTo || filterDriver || filterStatus) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setFilterDateFrom('');
                      setFilterDateTo('');
                      setFilterDriver('');
                      setFilterStatus('');
                      setSelectedRideIds([]);
                    }}
                  >
                    Filter zurücksetzen
                  </Button>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  {filteredRides.length} von {selectedOrder.rides?.length} Fahrten
                </Typography>
              </Box>

              {/* Aktions-Toolbar */}
              {selectedRideIds.length > 0 && (
                <Toolbar
                  sx={{
                    pl: 2, pr: 2, mb: 1,
                    bgcolor: 'action.selected',
                    borderRadius: 1,
                  }}
                >
                  <Typography sx={{ flex: '1 1 100%' }} variant="subtitle1">
                    {selectedRideIds.length} Fahrt{selectedRideIds.length !== 1 ? 'en' : ''} ausgewählt
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      if (selectedRideIds.length === 1) {
                        openRideDialog?.({ mode: 'edit', rideId: selectedRideIds[0] });
                      }
                    }}
                    disabled={selectedRideIds.length !== 1}
                    sx={{ mr: 1, whiteSpace: 'nowrap' }}
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<CancelIcon />}
                    onClick={() => handleBulkAction('cancel')}
                    sx={{ mr: 1, whiteSpace: 'nowrap' }}
                  >
                    Stornieren
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleBulkAction('delete')}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Löschen
                  </Button>
                </Toolbar>
              )}
              <StyledTableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedRideIds.length > 0 && selectedRideIds.length < filteredRides.length}
                          checked={filteredRides.length > 0 && selectedRideIds.length === filteredRides.length}
                          onChange={() => {
                            if (selectedRideIds.length === filteredRides.length) {
                              setSelectedRideIds([]);
                            } else {
                              setSelectedRideIds(filteredRides.map(r => r.id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Datum</TableCell>
                      <TableCell>Uhrzeit</TableCell>
                      <TableCell>Route</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Nachweis</TableCell>
                      <TableCell>Fahrer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRides.map((ride) => (
                      <TableRow
                        key={ride.id}
                        hover
                        selected={selectedRideIds.includes(ride.id)}
                        onClick={() => handleToggleRideSelect(ride.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedRideIds.includes(ride.id)} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatRideDateTime(ride.scheduledTime).toLocaleDateString('de-DE')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatRideDateTime(ride.scheduledTime).toLocaleTimeString('de-DE', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              <LocationIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                              {ride.start}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              → {ride.destination}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            label={getRideStatusLabel(ride.status)}
                            size="small"
                            status={ride.status}
                          />
                        </TableCell>
                        <TableCell>
                          {ride.medicalTransportOrderId ? (
                            <Chip
                              size="small"
                              label={ride.medicalTransportProofStatus || 'missing'}
                              color={ride.medicalTransportProofStatus === 'complete' ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {ride.driverName || 'Nicht zugewiesen'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            </>
          );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bestätigungs-Dialog für Bulk-Aktionen auf Einzelfahrten */}
      <Dialog open={ridesConfirmDialog} onClose={() => setRidesConfirmDialog(false)}>
        <DialogTitle>
          {selectedRideIds.length} Fahrt{selectedRideIds.length !== 1 ? 'en' : ''} {ridesActionType === 'delete' ? 'löschen' : 'stornieren'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie {selectedRideIds.length} ausgewählte Fahrt{selectedRideIds.length !== 1 ? 'en' : ''} {ridesActionType === 'delete' ? 'löschen' : 'stornieren'} möchten?
          </Typography>
          {ridesActionType === 'delete' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Die ausgewählten Fahrten werden unwiderruflich aus der Datenbank gelöscht!
            </Alert>
          )}
          {ridesActionType === 'cancel' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Die ausgewählten Fahrten werden storniert.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRidesConfirmDialog(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmBulkAction}
            color="error"
            variant="contained"
          >
            {ridesActionType === 'delete' ? 'Löschen' : 'Stornieren'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecurringOrdersList;
